import React, { useMemo } from 'react';
import {
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTranslatedMessages } from '../../../../hooks/useTranslatedMessages';

const REASON_PREVIEW_LENGTH = 80;

const previewReason = (reason) => {
  const text = reason ?? '';
  if (text.length <= REASON_PREVIEW_LENGTH) {
    return text;
  }
  return `${text.slice(0, REASON_PREVIEW_LENGTH)}…`;
};

const TopRejectionReasonsCard = ({ reasons = [], loading = false }) => {
  const { misDashboard: m } = useTranslatedMessages();

  const sortedReasons = useMemo(() => [...(reasons ?? [])].sort((a, b) => (b.count ?? 0) - (a.count ?? 0)), [reasons]);

  return (
    <Paper
      variant="outlined"
      sx={{
        height: '100%',
        width: '100%',
        borderRadius: 2,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        overflow: 'hidden',
      }}
    >
      <Typography variant="body2" fontWeight={500} sx={{ px: 2, pt: 2, pb: 1, flexShrink: 0 }}>
        {m.compliance?.rejection_reasons_title ?? 'Top Rejection Reasons'}
      </Typography>

      {loading ? (
        <Skeleton variant="rounded" height={220} sx={{ mx: 2, mb: 2 }} />
      ) : sortedReasons.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ px: 2, pb: 2 }}>
          {m.compliance?.no_rejection_reasons ?? 'No rejection reasons for the selected filters.'}
        </Typography>
      ) : (
        <TableContainer sx={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
          <Table size="small" sx={{ tableLayout: 'fixed' }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 72 }}>{m.compliance?.code ?? 'Code'}</TableCell>
                <TableCell>{m.compliance?.reason ?? 'Reason'}</TableCell>
                <TableCell align="right" sx={{ width: 112 }}>
                  {m.compliance?.occurrences ?? 'Occurrences'}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {sortedReasons.map((item) => (
                <TableRow key={`${item.code ?? ''}-${item.reason}`}>
                  <TableCell sx={{ fontSize: 14 }}>{item.code ?? ''}</TableCell>
                  <TableCell sx={{ fontSize: 14, overflow: 'hidden' }}>
                    <Tooltip
                      title={item.reason ?? ''}
                      placement="top-start"
                      enterDelay={300}
                      slotProps={{
                        tooltip: {
                          sx: {
                            maxWidth: 420,
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                          },
                        },
                      }}
                    >
                      <Typography
                        component="span"
                        sx={{
                          display: 'block',
                          fontSize: 14,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {previewReason(item.reason)}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontWeight: 600, fontSize: 14 }}
                  >
                    {item.count}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
};

export default TopRejectionReasonsCard;
